import type { ChannelContact, ChannelGroup, ChannelMessage, ChannelSettings, ChannelThread, ChannelView } from "@ark-bot/protocol";
import { CHANNEL_NOTE_MAX_CHARS } from "@ark-bot/protocol";
import { useEffect, useState } from "react";

import { api } from "../api.js";
import { shell } from "../host.js";
import { formatLocale, useText } from "../i18n.js";
import { WHATSAPP } from "../text/whatsapp.js";

import { ActiveGlide } from "./ActiveGlide.js";
import { Switch } from "./AgentSettings.js";

type Words = (typeof WHATSAPP)["en"];

/**
 * The agent's WhatsApp number (ARCHITECTURE §6.19): linking the phone, who it
 * answers and what it may say, and the conversations people have had with it.
 * Everything here is the agent's own; another agent's number is set up in
 * its own pane.
 */

const messageOf = (failure: unknown): string => (failure instanceof Error ? failure.message : String(failure));

/** "+62 812-5550-0142" and "081255500142" are both a number; what WhatsApp wants is the digits with the country code. */
export function phoneDigits(input: string): string {
  return input.replace(/\D+/g, "").replace(/^0+/, "");
}

export function formatPhone(number: string): string {
  return `+${number}`;
}

function WhatsAppLogo(): React.JSX.Element {
  return (
    <span className="wa-logo" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="20" height="20">
        <path d="M12 3.2a8.6 8.6 0 0 0-7.4 13l-1.2 4.4 4.5-1.2A8.6 8.6 0 1 0 12 3.2Z" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinejoin="round" />
        <circle cx="8.6" cy="12" r="1.1" fill="#fff" />
        <circle cx="12" cy="12" r="1.1" fill="#fff" />
        <circle cx="15.4" cy="12" r="1.1" fill="#fff" />
      </svg>
    </span>
  );
}

function badgeOf(view: ChannelView, t: Words): React.JSX.Element | null {
  // A connected row with a problem is one whose sending is paused while WhatsApp limits the number.
  if (view.state === "connected") return view.problem != null ? <span className="plugin-badge warn">{t.badgePaused}</span> : <span className="plugin-badge ok">{t.badgeConnected}</span>;
  if (view.state === "linking") return <span className="plugin-badge warn">{t.badgeLinking}</span>;
  if (view.state === "needs_attention") return <span className={`plugin-badge ${view.problem?.kind === "blocked" ? "bad" : "warn"}`}>{view.problem?.kind === "blocked" ? t.badgeBlocked : t.badgeNeedsAttention}</span>;
  return null;
}

function detailOf(view: ChannelView, agentName: string, t: Words): string {
  if (view.state === "connected" && view.problem != null) return view.problem.detail;
  if (view.state === "connected") {
    const connected = view.account == null ? t.connected : t.connectedAs(formatPhone(view.account.number));
    // A new number sends slowly for its first week, so WhatsApp gets used to it; say so, or the slowness reads as a fault.
    return view.warmingUntil == null ? connected : t.warming(connected, new Date(view.warmingUntil).toLocaleDateString(formatLocale(), { day: "numeric", month: "short" }));
  }
  if (view.state === "linking") return t.waitingForPhone;
  if (view.problem != null) return view.problem.detail;
  return t.giveNumber(agentName);
}

/** The Channels section of the Details pane: one row, opening the WhatsApp pane. */
export function ChannelsSummary({ agentId, agentName, revision, onOpen, onOpenConversations }: { readonly agentId: string; readonly agentName: string; readonly revision: number; readonly onOpen: () => void; readonly onOpenConversations: () => void }): React.JSX.Element | null {
  const t = useText(WHATSAPP);
  const [view, setView] = useState<ChannelView | null>(null);
  const [threads, setThreads] = useState<ChannelThread[]>([]);
  useEffect(() => {
    let cancelled = false;
    api
      .channels(agentId)
      .then((list) => {
        if (cancelled) return;
        const whatsapp = list.channels.find((channel) => channel.platform === "whatsapp") ?? null;
        setView(whatsapp);
        if (whatsapp?.available === true) return api.channelThreads(agentId).then((result) => !cancelled && setThreads(result.threads));
        return undefined;
      })
      .catch(() => {
        // An older box without the routes: no Channels section at all, as before.
        if (!cancelled) setView(null);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, revision]);
  if (view == null || !view.available) return null;
  const waiting = threads.reduce((sum, thread) => sum + thread.waiting, 0);
  return (
    <section className="details-section">
      <div className="details-section-head">
        <h3>{t.channels}</h3>
      </div>
      <p className="muted">{t.channelsAbout}</p>
      <div className={`wa-row${view.state === "needs_attention" ? " warn" : ""}`}>
        <WhatsAppLogo />
        <div className="wa-row-text">
          <div className="wa-row-name">
            <span>WhatsApp</span>
            {badgeOf(view, t)}
          </div>
          <div className="wa-row-detail muted">{detailOf(view, agentName, t)}</div>
        </div>
        <button type="button" className={view.state === "disconnected" ? "primary" : ""} onClick={onOpen}>
          {view.state === "disconnected" ? t.connect : view.state === "needs_attention" ? t.fix : t.open}
        </button>
      </div>
      {threads.length > 0 ? (
        <button type="button" className="wa-conversations-link" onClick={onOpenConversations}>
          <span>{t.conversations(threads.length)}</span>
          {waiting > 0 ? <span className="plugin-badge warn">{t.waitingForYou(waiting)}</span> : null}
          <span className="muted" aria-hidden="true">
            ›
          </span>
        </button>
      ) : null}
    </section>
  );
}

// --- linking ---------------------------------------------------------------------

function Requirements({ agentName, onReady }: { readonly agentName: string; readonly onReady: () => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  const [ticked, setTicked] = useState(false);
  return (
    <div className="wa-step">
      <ul className="wa-reqs">
        <li>{t.reqNumber(agentName)}</li>
        <li>{t.reqApp}</li>
        <li>{t.reqOnline}</li>
      </ul>
      <div className="wa-caution">
        <strong>{t.beforeYouLink}</strong>
        <span>{t.caution(agentName)}</span>
      </div>
      {/* The disclaimer every unofficial WhatsApp service in Indonesia carries (Fonnte's FAQ, for one): not WhatsApp's, not its API, and a ban is the user's risk. */}
      <p className="wa-disclaimer muted">{t.disclaimer}</p>
      <label className="wa-check">
        <input type="checkbox" checked={ticked} onChange={(event) => setTicked(event.target.checked)} />
        {t.accept(agentName)}
      </label>
      <button type="button" className="primary wa-wide" disabled={!ticked} onClick={onReady}>
        {t.showCode}
      </button>
    </div>
  );
}

function Linking({ agentId, agentName, view, onView }: { readonly agentId: string; readonly agentName: string; readonly view: ChannelView; readonly onView: (view: ChannelView) => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  // You cannot scan the screen you are holding: the packaged app starts on the code.
  const [method, setMethod] = useState<"qr" | "code">(view.link?.method ?? (shell() ? "code" : "qr"));
  const [phone, setPhone] = useState(view.link?.method === "code" ? formatPhone(view.link.phone) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async (request: { method: "qr" } | { method: "code"; phone: string }): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onView(await api.linkChannel(agentId, request));
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  // The QR tab asks for a code as soon as it is shown, unless one is already
  // on its way. Only when the tab changes: each new code arrives by the event
  // stream, which refetches the view.
  const linkingByQr = view.state === "linking" && view.link?.method === "qr";
  useEffect(() => {
    if (method === "qr" && !linkingByQr) void start({ method: "qr" });
  }, [method]);

  const cancel = async (): Promise<void> => {
    setBusy(true);
    try {
      onView(await api.unlinkChannel(agentId));
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
    }
  };

  const digits = phoneDigits(phone);
  const link = view.state === "linking" ? view.link : undefined;
  return (
    <div className="wa-step">
      <div className="wa-seg" role="tablist" aria-label={t.howToLink}>
        <ActiveGlide selector='[aria-selected="true"]' className="glide-seg" />
        <button type="button" role="tab" aria-selected={method === "qr"} onClick={() => setMethod("qr")}>
          {t.scanQr}
        </button>
        <button type="button" role="tab" aria-selected={method === "code"} onClick={() => setMethod("code")}>
          {t.useCode}
        </button>
      </div>
      {method === "qr" ? (
        <>
          <div className="wa-qr-wrap">
            {link?.method === "qr" ? (
              <img className="wa-qr" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(link.qrSvg)}`} alt={t.qrAlt} />
            ) : (
              <div className="wa-qr wa-qr-waiting muted">{busy || view.state === "linking" ? t.gettingCode : t.noCode}</div>
            )}
            {link?.method === "qr" ? (
              <span className="muted wa-small">{t.codeChanges}</span>
            ) : view.state !== "linking" && !busy ? (
              // Nobody scanned in time and WhatsApp stopped offering codes: one press asks again.
              <button type="button" onClick={() => void start({ method: "qr" })}>
                {t.newCode}
              </button>
            ) : null}
          </div>
          {/* WhatsApp's own menu names, as its app shows them in each language. */}
          <ol className="wa-steps">
            <li>
              {t.stepOpen(agentName)}
              <b>WhatsApp</b>
            </li>
            <li>
              {t.stepGoTo}
              <b>{t.menuLinkedDevices}</b>
            </li>
            <li>
              {t.stepTap}
              <b>{t.linkDevice}</b>
              {t.stepPoint}
            </li>
          </ol>
        </>
      ) : (
        <>
          <form
            className="wa-phone"
            onSubmit={(event) => {
              event.preventDefault();
              if (digits.length >= 7) void start({ method: "code", phone: digits });
            }}
          >
            <label>
              {t.phoneLabel(agentName)}
              <input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+62 812 5550 0142" inputMode="tel" autoComplete="off" />
            </label>
            <button type="submit" className="primary" disabled={busy || digits.length < 7}>
              {busy ? t.asking : t.getCode}
            </button>
          </form>
          {link?.method === "code" ? (
            <>
              {link.code == null ? (
                <div className="muted">{t.askingWhatsApp}</div>
              ) : (
                <div className="wa-code" aria-label={t.codeLabel(link.code)}>
                  {link.code.split("-").map((part) => (
                    <span key={part}>{part}</span>
                  ))}
                </div>
              )}
              <ol className="wa-steps">
                <li>
                  {t.codeStepOn(agentName)}
                  <b>WhatsApp → {t.menuLinkedDevices}</b>
                </li>
                <li>
                  {t.stepTap}
                  <b>{t.linkDevice}</b>
                  {t.stepThen}
                  <b>{t.linkWithPhone}</b>
                </li>
                <li>{t.typeCode}</li>
              </ol>
            </>
          ) : null}
        </>
      )}
      {error != null ? <div className="settings-error">{error}</div> : null}
      <button type="button" className="quiet" disabled={busy} onClick={() => void cancel()}>
        {t.cancel}
      </button>
    </div>
  );
}

// --- settings ----------------------------------------------------------------------

function KnownList({ known, onChange }: { readonly known: readonly ChannelContact[]; readonly onChange: (known: ChannelContact[]) => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const digits = phoneDigits(number);
  return (
    <div className="wa-known">
      {known.length === 0 ? <p className="muted wa-small">{t.nobodyYet}</p> : null}
      <ul>
        {known.map((contact) => (
          <li key={contact.number}>
            <span>
              <strong>{contact.name ?? formatPhone(contact.number)}</strong>
              {contact.name == null ? null : <span className="muted"> {formatPhone(contact.number)}</span>}
            </span>
            <button type="button" className="quiet" onClick={() => onChange(known.filter((other) => other.number !== contact.number))} aria-label={t.takeOff(contact.name ?? contact.number)}>
              {t.remove}
            </button>
          </li>
        ))}
      </ul>
      <div className="wa-known-add">
        <input value={name} onChange={(event) => setName(event.target.value)} placeholder={t.namePlaceholder} aria-label={t.theirName} />
        <input value={number} onChange={(event) => setNumber(event.target.value)} placeholder="+62 811 …" inputMode="tel" aria-label={t.theirNumber} />
        <button
          type="button"
          disabled={digits.length < 7}
          onClick={() => {
            onChange([...known.filter((other) => other.number !== digits), { number: digits, ...(name.trim().length > 0 ? { name: name.trim() } : {}) }]);
            setName("");
            setNumber("");
          }}
        >
          {t.add}
        </button>
      </div>
    </div>
  );
}

/** The groups the agent answers in. Groups are added from their conversation, once someone there has mentioned the agent; here they can only be taken off. */
function GroupList({ groups, agentName, onChange }: { readonly groups: readonly ChannelGroup[]; readonly agentName: string; readonly onChange: (groups: ChannelGroup[]) => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  return (
    <div className="wa-known">
      <ul>
        {groups.map((group) => (
          <li key={group.address}>
            <strong>{group.name}</strong>
            <button type="button" className="quiet" onClick={() => onChange(groups.filter((other) => other.address !== group.address))} aria-label={t.stopAnsweringIn(group.name)}>
              {t.remove}
            </button>
          </li>
        ))}
      </ul>
      <p className="muted wa-small">
        {groups.length === 0 ? t.noGroupsYet : ""}
        {t.groupsHint(agentName)}
      </p>
    </div>
  );
}

const WEEK: ReadonlyArray<readonly [number, (t: Words) => string]> = [
  [1, (t) => t.mon],
  [2, (t) => t.tue],
  [3, (t) => t.wed],
  [4, (t) => t.thu],
  [5, (t) => t.fri],
  [6, (t) => t.sat],
  [0, (t) => t.sun],
];

type CalendarState = "checking" | "connected" | "not-connected" | "no-plugins";

/** Whether the user's Google Calendar is connected in Plugins, which is where free times come from. */
function useCalendarState(): CalendarState {
  const [state, setState] = useState<CalendarState>("checking");
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const status = await api.pluginsStatus();
        if (!status.configured) {
          if (live) setState("no-plugins");
          return;
        }
        const { connections } = await api.pluginConnections();
        if (live) setState(connections.some((connection) => connection.toolkit === "googlecalendar" && connection.state === "active") ? "connected" : "not-connected");
      } catch {
        if (live) setState("no-plugins");
      }
    })();
    return () => {
      live = false;
    };
  }, []);
  return state;
}

/** Who hears free times, holding meetings, and the hours offered: all of it reads the calendar through a filter that gives times only. */
function CalendarSettings({ agentName, draft, onChange }: { readonly agentName: string; readonly draft: ChannelSettings; readonly onChange: (settings: ChannelSettings) => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  const calendar = useCalendarState();
  const hours = draft.meetingHours;
  const line = calendar === "connected" ? t.calendarConnected : calendar === "not-connected" ? t.calendarNotConnected(agentName) : calendar === "no-plugins" ? t.calendarNoPlugins(agentName) : t.checkingCalendar;
  const audience: ReadonlyArray<readonly [ChannelSettings["freeTimes"], string, string]> = [
    ["off", t.audienceOff, t.audienceOffDetail(agentName)],
    ["known", t.peopleYouKnow, t.audienceKnownDetail],
    ["everyone", t.audienceEveryone, t.audienceEveryoneDetail],
  ];
  return (
    <>
      <h3 className="wa-section-title">{t.yourCalendar}</h3>
      <p className="wa-hint">{line}</p>
      <fieldset className="wa-radios">
        <legend>{t.whoMayHear}</legend>
        {audience.map(([value, title, detail]) => (
          <label key={value} className={draft.freeTimes === value ? "on" : ""}>
            <input type="radio" name="freeTimes" checked={draft.freeTimes === value} onChange={() => onChange({ ...draft, freeTimes: value })} />
            <span>
              {title}
              <small>{detail}</small>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="settings-card">
        <div className="settings-card-text">
          <strong>{t.holdMeetings}</strong>
          <span className="muted">{t.holdMeetingsDetail(agentName)}</span>
        </div>
        <Switch checked={draft.meetings && draft.freeTimes !== "off"} onChange={(meetings) => onChange({ ...draft, meetings, ...(meetings && draft.freeTimes === "off" ? { freeTimes: "known" as const } : {}) })} label={t.holdMeetings} />
      </div>
      <div className="wa-field">
        <span className="wa-field-label">{t.hoursLabel}</span>
        <div className="wa-days" role="group" aria-label={t.daysLabel}>
          {WEEK.map(([day, label]) => {
            const on = hours.days.includes(day);
            return (
              <button key={day} type="button" aria-pressed={on} onClick={() => onChange({ ...draft, meetingHours: { ...hours, days: on ? hours.days.filter((other) => other !== day) : [...hours.days, day].sort() } })}>
                {label(t)}
              </button>
            );
          })}
        </div>
        <div className="wa-hours">
          <input type="time" value={hours.start} step={900} onChange={(event) => onChange({ ...draft, meetingHours: { ...hours, start: event.target.value } })} aria-label={t.from} />
          <span className="muted">{t.to}</span>
          <input type="time" value={hours.end} step={900} onChange={(event) => onChange({ ...draft, meetingHours: { ...hours, end: event.target.value } })} aria-label={t.until} />
        </div>
        {hours.start >= hours.end ? <span className="wa-hint">{t.endAfterStart}</span> : <span className="wa-hint">{t.onYourClock}</span>}
      </div>
    </>
  );
}

/** The settings with the name set, or without it when the box is emptied. */
function withOwnerName(settings: ChannelSettings, name: string): ChannelSettings {
  const { ownerName: _previous, ...rest } = settings;
  return name.length > 0 ? { ...rest, ownerName: name } : rest;
}

function Settings({ agentId, agentName, view, onView }: { readonly agentId: string; readonly agentName: string; readonly view: ChannelView; readonly onView: (view: ChannelView) => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  const [draft, setDraft] = useState<ChannelSettings>(view.settings);
  const [ownerNumber, setOwnerNumber] = useState(view.settings.ownerNumber == null ? "" : formatPhone(view.settings.ownerNumber));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const owner = phoneDigits(ownerNumber);

  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      onView(
        await api.updateChannelSettings(agentId, {
          ownerNumber: owner.length === 0 ? null : owner,
          ownerName: draft.ownerName == null || draft.ownerName.trim().length === 0 ? null : draft.ownerName.trim(),
          unknown: draft.unknown,
          known: draft.known,
          groups: draft.groups,
          allowedGroups: draft.allowedGroups,
          note: draft.note,
          urgentToPhone: draft.urgentToPhone,
          freeTimes: draft.freeTimes,
          meetings: draft.meetings,
          meetingHours: draft.meetingHours,
        }),
      );
      setSaved(true);
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="profile-form wa-settings" onSubmit={(event) => void save(event)}>
      <label>
        {t.ownNumber}
        <input value={ownerNumber} onChange={(event) => setOwnerNumber(event.target.value)} placeholder="+62 811 2000 3141" inputMode="tel" autoComplete="off" />
        <span className="wa-hint">{t.ownNumberHint}</span>
      </label>
      <label>
        {t.knownAs}
        <input value={draft.ownerName ?? ""} onChange={(event) => setDraft(withOwnerName(draft, event.target.value))} placeholder="Raymond" maxLength={80} />
        <span className="wa-hint">{t.knownAsHint(agentName)}</span>
      </label>
      <fieldset className="wa-radios">
        <legend>{t.unknownPeople}</legend>
        <label className={draft.unknown === "take_message" ? "on" : ""}>
          <input type="radio" name="unknown" checked={draft.unknown === "take_message"} onChange={() => setDraft({ ...draft, unknown: "take_message" })} />
          <span>
            {t.takeMessage}
            <small>{t.takeMessageDetail(agentName)}</small>
          </span>
        </label>
        <label className={draft.unknown === "ignore" ? "on" : ""}>
          <input type="radio" name="unknown" checked={draft.unknown === "ignore"} onChange={() => setDraft({ ...draft, unknown: "ignore" })} />
          <span>
            {t.stayQuiet}
            <small>{t.stayQuietDetail}</small>
          </span>
        </label>
      </fieldset>
      <div className="wa-field">
        <span className="wa-field-label">{t.peopleYouKnow}</span>
        <KnownList known={draft.known} onChange={(known) => setDraft({ ...draft, known })} />
      </div>
      <label>
        {t.mayTell(agentName)}
        <textarea rows={6} value={draft.note} maxLength={CHANNEL_NOTE_MAX_CHARS} onChange={(event) => setDraft({ ...draft, note: event.target.value })} placeholder={t.notePlaceholder} />
        <span className="wa-hint">{t.noteHint(agentName)}</span>
      </label>
      <CalendarSettings agentName={agentName} draft={draft} onChange={setDraft} />
      <h3 className="wa-section-title">{t.everythingElse}</h3>
      <div className="settings-card">
        <div className="settings-card-text">
          <strong>{t.urgentNow}</strong>
          <span className="muted">{t.urgentNowDetail(agentName)}</span>
        </div>
        <Switch checked={draft.urgentToPhone} onChange={(urgentToPhone) => setDraft({ ...draft, urgentToPhone })} label={t.urgentSwitch} />
      </div>
      <div className="settings-card">
        <div className="settings-card-text">
          <strong>{t.answerGroups}</strong>
          <span className="muted">{t.answerGroupsDetail(agentName)}</span>
        </div>
        <Switch checked={draft.groups} onChange={(groups) => setDraft({ ...draft, groups })} label={t.answerGroups} />
      </div>
      {draft.groups ? (
        <div className="wa-field">
          <span className="wa-field-label">{t.groupsAnswers(agentName)}</span>
          <GroupList groups={draft.allowedGroups ?? []} agentName={agentName} onChange={(allowedGroups) => setDraft({ ...draft, allowedGroups })} />
        </div>
      ) : null}
      {error != null ? <div className="settings-error">{error}</div> : null}
      <div className="settings-actions">
        <button type="submit" className="primary" disabled={saving || (owner.length > 0 && owner.length < 7) || draft.meetingHours.start >= draft.meetingHours.end}>
          {saving ? t.saving : t.save}
        </button>
        {saved ? <span className="muted settings-note">{t.savedNote}</span> : null}
      </div>
    </form>
  );
}

/** The WhatsApp pane: linking while there is no number, the settings once there is. */
export function WhatsAppPane({ agentId, agentName, revision, onOpenConversations }: { readonly agentId: string; readonly agentName: string; readonly revision: number; readonly onOpenConversations: () => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  const [view, setView] = useState<ChannelView | null>(null);
  const [ready, setReady] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threads, setThreads] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .channels(agentId)
      .then((list) => {
        if (!cancelled) setView(list.channels.find((channel) => channel.platform === "whatsapp") ?? null);
      })
      .catch((failure: unknown) => !cancelled && setError(messageOf(failure)));
    api
      .channelThreads(agentId)
      .then((result) => !cancelled && setThreads(result.threads.length))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [agentId, revision]);

  const act = async (work: () => Promise<ChannelView>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      setView(await work());
    } catch (failure) {
      setError(messageOf(failure));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (view == null) return <div className="details-body">{error != null ? <div className="settings-error">{error}</div> : <div className="muted">{t.loading}</div>}</div>;
  if (!view.available) {
    return (
      <div className="details-body">
        <p className="muted">{t.notAvailable}</p>
      </div>
    );
  }

  const linked = view.state === "connected" || view.state === "needs_attention";
  const canRelink = view.state === "needs_attention" && view.problem?.kind !== "failed";
  return (
    <div className="details-body wa-pane">
      {linked ? (
        <div className={`wa-row${view.state === "needs_attention" ? " warn" : ""}`}>
          <WhatsAppLogo />
          <div className="wa-row-text">
            <div className="wa-row-name">
              <span>WhatsApp</span>
              {badgeOf(view, t)}
            </div>
            <div className="wa-row-detail muted">{detailOf(view, agentName, t)}</div>
          </div>
          {confirming ? (
            <>
              <button type="button" className="danger" disabled={busy} onClick={() => void act(() => api.unlinkChannel(agentId))}>
                {t.disconnect}
              </button>
              <button type="button" className="quiet" onClick={() => setConfirming(false)}>
                {t.keep}
              </button>
            </>
          ) : (
            <button type="button" className="quiet" disabled={busy} onClick={() => setConfirming(true)}>
              {t.disconnect}
            </button>
          )}
        </div>
      ) : null}
      {view.state === "needs_attention" ? (
        <div className="wa-fix">
          {canRelink ? (
            <button type="button" className="primary" disabled={busy} onClick={() => setView({ ...view, state: "disconnected" })}>
              {view.problem?.kind === "blocked" ? t.useAnother : t.linkAgain}
            </button>
          ) : (
            <button type="button" disabled={busy} onClick={() => void act(() => api.refreshChannel(agentId))}>
              {t.tryAgainNow}
            </button>
          )}
        </div>
      ) : null}
      {view.state === "disconnected" && view.problem != null ? <div className="wa-problem">{view.problem.detail}</div> : null}
      {view.state === "disconnected" && !ready ? <Requirements agentName={agentName} onReady={() => setReady(true)} /> : null}
      {/* One element for both states, so the tab the user picked survives the link starting. */}
      {view.state === "linking" || (view.state === "disconnected" && ready) ? <Linking agentId={agentId} agentName={agentName} view={view} onView={setView} /> : null}
      {error != null ? <div className="settings-error">{error}</div> : null}
      {linked ? (
        <>
          {threads > 0 ? (
            <button type="button" className="wa-conversations-link" onClick={onOpenConversations}>
              <span>{t.conversations(threads)}</span>
              <span className="muted" aria-hidden="true">
                ›
              </span>
            </button>
          ) : null}
          <Settings key={JSON.stringify(view.settings)} agentId={agentId} agentName={agentName} view={view} onView={setView} />
          <p className="wa-foot muted">{t.paneFoot(agentName)}</p>
        </>
      ) : null}
    </div>
  );
}

// --- conversations ------------------------------------------------------------------

function when(ms: number): string {
  const date = new Date(ms);
  const today = new Date();
  return date.toDateString() === today.toDateString() ? date.toLocaleTimeString(formatLocale(), { hour: "2-digit", minute: "2-digit" }) : date.toLocaleDateString(formatLocale(), { day: "numeric", month: "short" });
}

const TIER_LABEL: Record<ChannelThread["tier"], (t: Words) => string> = { known: (t) => t.tierKnown, unknown: (t) => t.tierUnknown, group: (t) => t.tierGroup };

function preview(message: ChannelMessage, t: Words): string {
  if (message.kind === "voice") return t.voiceNote;
  if (message.kind === "image") return message.text.length > 0 ? t.photoWith(message.text) : t.photo;
  if (message.kind === "file") return message.text.length > 0 ? t.fileWith(message.text) : t.file;
  if (message.kind === "other" && message.text.length === 0) return t.unshowable;
  return message.text;
}

function Thread({ agentId, threadId, revision }: { readonly agentId: string; readonly threadId: string; readonly revision: number }): React.JSX.Element {
  const t = useText(WHATSAPP);
  const [thread, setThread] = useState<ChannelThread | null>(null);
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .channelThread(agentId, threadId)
      .then((result) => {
        if (cancelled) return;
        setThread(result.thread);
        setMessages(result.messages);
      })
      .catch((failure: unknown) => !cancelled && setError(messageOf(failure)));
    return () => {
      cancelled = true;
    };
  }, [agentId, threadId, revision]);

  const update = async (request: Parameters<typeof api.updateChannelThread>[2]): Promise<void> => {
    setError(null);
    try {
      setThread(await api.updateChannelThread(agentId, threadId, request));
    } catch (failure) {
      setError(messageOf(failure));
    }
  };
  const setKnown = (known: boolean): Promise<void> => update({ known, ...(known && name.trim().length > 0 ? { name: name.trim() } : {}) });

  if (thread == null) return <div className="details-body">{error != null ? <div className="settings-error">{error}</div> : <div className="muted">{t.loading}</div>}</div>;
  return (
    <div className="details-body wa-thread">
      <div className="wa-thread-head">
        <strong>{thread.name}</strong>
        <span className="muted">
          {thread.number == null ? "" : `${formatPhone(thread.number)} · `}
          {TIER_LABEL[thread.tier](t)}
          {thread.pushName != null && thread.pushName !== thread.name ? t.callsThemselves(thread.pushName) : ""}
        </span>
      </div>
      {thread.tier === "unknown" && thread.number != null ? (
        <div className="wa-know">
          <input value={name} onChange={(event) => setName(event.target.value)} placeholder={t.yourNameForThem} aria-label={t.yourNameForThem} maxLength={80} />
          <button type="button" onClick={() => void setKnown(true)}>
            {t.addToKnown}
          </button>
        </div>
      ) : thread.tier === "known" ? (
        <button type="button" className="quiet wa-unknow" onClick={() => void setKnown(false)}>
          {t.takeOffList}
        </button>
      ) : thread.tier === "group" && thread.allowed === false ? (
        <div className="wa-group-allow">
          <span className="wa-hint">{t.groupNotAnswered}</span>
          <button type="button" onClick={() => void update({ allowed: true })}>
            {t.answerInGroup}
          </button>
        </div>
      ) : thread.tier === "group" && thread.allowed === true ? (
        <button type="button" className="quiet wa-unknow" onClick={() => void update({ allowed: false })}>
          {t.stopInGroup}
        </button>
      ) : null}
      {error != null ? <div className="settings-error">{error}</div> : null}
      <div className="wa-messages">
        {messages.map((message) => (
          <div key={message.id} className={`wa-message ${message.direction}${message.failed === true ? " failed" : ""}`}>
            {message.sender != null ? <span className="wa-sender">{message.sender}</span> : null}
            <span className="wa-text">{preview(message, t)}</span>
            <span className="wa-meta">
              {message.byOwner === true ? t.forYou : ""}
              {message.failed === true ? t.notDelivered : ""}
              {when(message.at)}
            </span>
          </div>
        ))}
      </div>
      <p className="wa-foot muted">{thread.tier === "group" ? t.threadFootGroup : t.threadFoot(thread.name)}</p>
    </div>
  );
}

/** Everyone who has written to the agent's number, newest first; one opened reads their conversation. */
export function Conversations({ agentId, revision, threadId, onThread }: { readonly agentId: string; readonly revision: number; readonly threadId: string | null; readonly onThread: (threadId: string | null) => void }): React.JSX.Element {
  const t = useText(WHATSAPP);
  const [threads, setThreads] = useState<ChannelThread[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .channelThreads(agentId)
      .then((result) => !cancelled && setThreads(result.threads))
      .catch((failure: unknown) => !cancelled && setError(messageOf(failure)));
    return () => {
      cancelled = true;
    };
  }, [agentId, revision]);
  if (threadId != null) return <Thread agentId={agentId} threadId={threadId} revision={revision} />;
  return (
    <div className="details-body">
      {error != null ? <div className="settings-error">{error}</div> : null}
      {threads == null ? <div className="muted">{t.loading}</div> : null}
      {threads != null && threads.length === 0 ? <p className="muted">{t.nobodyWritten}</p> : null}
      {threads != null && threads.length > 0 ? (
        <ul className="wa-threads">
          {threads.map((thread) => (
            <li key={thread.id}>
              <button type="button" onClick={() => onThread(thread.id)}>
                <span className={`wa-avatar tier-${thread.tier}`} aria-hidden="true">
                  {thread.tier === "group" ? "#" : thread.tier === "unknown" && thread.name.startsWith("+") ? "+" : thread.name.charAt(0).toUpperCase()}
                </span>
                <span className="wa-thread-body">
                  <span className="wa-thread-top">
                    <strong>{thread.name}</strong>
                    <span className="plugin-badge plain">{TIER_LABEL[thread.tier](t)}</span>
                    {thread.allowed === false ? <span className="plugin-badge warn">{t.notAnswered}</span> : null}
                    <span className="muted wa-when">{when(thread.lastAt)}</span>
                  </span>
                  <span className="wa-thread-line muted">
                    {thread.lastDirection === "out" ? t.replied : ""}
                    {thread.lastText}
                  </span>
                  {thread.waiting > 0 ? <span className="wa-thread-state">{t.waitingForYou(thread.waiting)}</span> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
